import QRCode from "qrcode";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { getType, PDF_PAGE_SIZE } from "../../util"
import {
    formatDate12h,
    getInspectorName,
    getOperatorName,
    getStatusLabel,
    resolveOdometerUnit,
    stripOdometerSuffix,
} from "../../reports/inspectionReport"
pdfMake.vfs = pdfFonts.pdfMake.vfs;

function compare(a, b){
    if (a.date > b.date) return -1;
    if (b.date > a.date) return 1;

    return 0;
}

function measureImage(dataUrl) {
    if (!dataUrl) {
        return Promise.resolve({ width: 1, height: 1 })
    }

    return new Promise((resolve) => {
        const image = new Image()
        image.onload = () => resolve({ width: image.width || 1, height: image.height || 1 })
        image.onerror = () => resolve({ width: 1, height: 1 })
        image.src = dataUrl
    })
}

export async function generateQR(uId, vehicleDict, customInspectionDict, vIds) {
    const opts = {
        errorCorrectionLevel: 'H',
        level: 6,
        margin:2,
        scale: 20,
    }
    
    const optsCustom = {
        errorCorrectionLevel: 'H',
        level: 8,
        margin:2,
        scale: 20,
    }

    var docDefinition = {
        content: [],
        styles: {
            header: {
                fontSize: 12,
                bold: true,
                margin: [30, 0, 0, 0],
                color: 'grey'
            },
            subheader: {
                fontSize: 12,
                bold: true,
                margin: [0, 10, 0, 5]
            },
            tableExample: {
                margin: [0, 5, 0, 15]
            },
            tableHeader: {
                bold: true,
                fontSize: 13,
                color: 'black'
            }
        },
        defaultStyle: {
            // alignment: 'justify'
        }
    }

    for (var ind = 0; ind < vIds.length; ind++) {
        const vehicle = vehicleDict[vIds[ind].id]
        const customInspection = customInspectionDict[vehicle.vehicleData.type]

        var data = "02CC"
        data += customInspection.fallback
        data += vehicle.id
        data += uId
        data += customInspection.id
        
        const image = (await QRCode.toDataURL(data, customInspection.id === "0" ? opts : optsCustom))
        
        docDefinition.content.push(
            { text: vehicle.vehicleData.unitNumber, style: 'header' },
            { text: ' Make/Model: ' + vehicle.vehicleData.make + ' License: ' + vehicle.vehicleData.lic, style: 'header' },
            { image: image, width: 525 },
        )

        if (ind + 1 < vIds.length) {
            docDefinition.content.push({ text:"", pageBreak: 'before' })
        }
    }

    const fileName = "Batch-of-" + String(vIds.length) + "-QRCodes.pdf";

    pdfMake.createPdf(docDefinition).download(fileName);
}

export async function generatePDF(rawInspections, customDict, startDate, endDate, workorder, getPhoto, onProgress) {
    
    if (rawInspections.length === 0) {
        return 0
    }

    const inspections = rawInspections.sort(compare)
    const totalInspections = inspections.length
    const totalSteps = inspections.reduce(
        (stepCount, inspection) => stepCount + 1 + inspection.items.length,
        0,
    )
    let completedSteps = 0

    const reportProgress = (currentInspection, processedInspections, stage) => {
        if (typeof onProgress !== 'function') {
            return
        }

        onProgress({
            currentInspection,
            processedInspections,
            totalInspections,
            completedSteps,
            totalSteps,
            stage,
        })
    }

    reportProgress(1, 0, 'inspection')

    var lc = 6;
    const vehicleData = inspections[0].vehicleData;

    var docDefinition = {
        content: [
            { text: 'Audit for ' + (vehicleData.unitNumber), style: 'subheader' },
            { text: 'Between Dates: ' + startDate.toDateString() + " - " + endDate.toDateString(), style: 'subheader' },
            'Jur/Lic/VIN:  ' + (vehicleData.jurisdiction) + " / " + (vehicleData.lic) + " / " + (vehicleData.vin),
            'Type/Make/Year:  ' + getType(vehicleData.type, customDict) + " / " + (vehicleData.make) + " / " + (vehicleData.year),
        ],
        styles: {
            header: {
                fontSize: 18,
                bold: true,
                margin: [0, 0, 0, 10]
            },
            subheader: {
                fontSize: 14,
                bold: true,
                margin: [0, 10, 0, 5]
            },
            tableExample: {
                margin: [0, 5, 0, 15]
            },
            tableHeader: {
                bold: true,
                fontSize: 13,
                color: 'black'
            },
            inspectionTitle: {
                fontSize: 16,
                bold: true,
                margin: [0, 12, 0, 4],
            },
            vehicleTypeLabel: {
                fontSize: 11,
                italics: true,
                color: 'grey',
                margin: [0, 0, 0, 6],
            },
            defectSummary: {
                fontSize: 11,
                margin: [0, 6, 0, 4],
            },
            defectCaption: {
                fontSize: 11,
                bold: true,
                margin: [0, 0, 0, 4],
            },
            regFootnote: {
                fontSize: 8,
                italics: true,
                color: 'grey',
                margin: [0, 4, 0, 0],
            },
            signatureLine: {
                fontSize: 10,
                margin: [0, 8, 0, 4],
            },
            copyright: {
                fontSize: 8,
                color: 'grey',
                margin: [0, 6, 0, 0],
            },
        },
        defaultStyle: {
            // alignment: 'justify'
        }
    };

    for (var ind = 0; ind < inspections.length; ind++) {
        const inspection = inspections[ind];
        const currentInspection = ind + 1
        const signature = (await getPhoto(inspection.metadata.signature))
        completedSteps += 1
        reportProgress(currentInspection, ind, 'inspection')

        const vehicleTypeString = getType(vehicleData.type, customDict)
        const inspectorName = getInspectorName(inspection.metadata)
        const operatorName = getOperatorName(inspection.metadata)
        const { formattedDate, time } = formatDate12h(inspection.UTCdate)
        const overallStatus = getStatusLabel(
            inspection.statusCode != null ? inspection.statusCode : inspection.status
        )
        const odoUnit = resolveOdometerUnit(vehicleData, inspection.vehicleType)
        const odoValue = stripOdometerSuffix(
            inspection.odometerRaw != null ? inspection.odometerRaw : inspection.odometer
        )
        const location = inspection.metadata.location || ''

        lc += 22
        if (!workorder && lc > PDF_PAGE_SIZE) {
            docDefinition.content.push({ text: "", pageBreak: 'before' })
            lc = 0
        }

        docDefinition.content.push(
            { text: vehicleTypeString, style: 'vehicleTypeLabel' },
            { text: 'Inspection Report', style: 'inspectionTitle' },
            {
                style: 'tableExample',
                table: {
                    widths: ['*', '*', '*'],
                    body: [[
                        { text: [{ text: 'Inspected by: ', bold: true }, inspectorName] },
                        { text: [{ text: 'Date: ', bold: true }, formattedDate] },
                        { text: [{ text: 'Time: ', bold: true }, time] },
                    ]],
                },
                layout: 'lightHorizontalLines',
            },
            {
                style: 'tableExample',
                table: {
                    widths: ['*', '*'],
                    body: [
                        [
                            { text: [{ text: 'Serial/VIN number: ', bold: true }, vehicleData.vin || 'Not Available'], colSpan: 2 },
                            {},
                        ],
                        [
                            { text: [{ text: 'Unit number: ', bold: true }, vehicleData.unitNumber || 'Not Available'], colSpan: 2 },
                            {},
                        ],
                        [
                            { text: [{ text: 'Operator Name: ', bold: true }, operatorName], colSpan: 2 },
                            {},
                        ],
                        [
                            { text: [{ text: 'Location: ', bold: true }, location] },
                            { text: [{ text: 'Odometer: ', bold: true }, `${odoValue} ${odoUnit}`] },
                        ],
                        [
                            { text: [{ text: 'Make/Model: ', bold: true }, vehicleData.make || 'Not Available'] },
                            { text: [{ text: 'Year: ', bold: true }, `${vehicleData.year ?? 'Not Available'}`] },
                        ],
                        [
                            { text: [{ text: 'License plate number: ', bold: true }, vehicleData.lic || 'Not Available'] },
                            { text: [{ text: 'Jurisdiction: ', bold: true }, vehicleData.jurisdiction || 'Not Available'] },
                        ],
                        [
                            { text: [{ text: 'Reported as: ', bold: true }, overallStatus], colSpan: 2 },
                            {},
                        ],
                    ],
                },
                layout: 'lightHorizontalLines',
            }
        )

        let majorCount = 0
        let minorCount = 0
        for (const it of inspection.items) {
            const s = getStatusLabel(it.condition)
            if (s === 'Action Required') majorCount++
            else if (s === 'Monitor') minorCount++
        }
        const defectCount = majorCount + minorCount
        const summaryLines = []
        if (defectCount === 0) {
            summaryLines.push('No major \u2714 no minor \u2714 defects found during inspection.')
        } else {
            if (majorCount > 0) summaryLines.push(`Major defects found \u2716 (${majorCount})`)
            if (minorCount > 0) summaryLines.push(`Minor defects found \u2716 (${minorCount})`)
        }
        docDefinition.content.push({ text: summaryLines.join('\n'), style: 'defectSummary' })
        docDefinition.content.push({ text: `${defectCount} Defect(s) found`, style: 'defectCaption' })

        const defectHeader = [
            { text: 'Description', style: 'tableHeader' },
            { text: 'Status', style: 'tableHeader' },
            { text: 'Comments', style: 'tableHeader' },
            { text: 'Photo', style: 'tableHeader' },
        ]

        if (inspection.items.length === 0) {
            docDefinition.content.push({
                style: 'tableExample',
                table: {
                    widths: [120, 60, 180, 140],
                    body: [
                        defectHeader,
                        [
                            { text: 'No defects recorded', colSpan: 4, italics: true, color: 'grey' },
                            {}, {}, {},
                        ],
                    ],
                },
            })
        }

        for (let iInd = 0; iInd < inspection.items.length; iInd++) {
            const item = inspection.items[iInd]
            const photo = (await getPhoto(item.photoKey))
            completedSteps += 1
            reportProgress(currentInspection, ind, 'photo')

            const { width, height } = await measureImage(photo)
            const commentLength = item.comment ? item.comment.length : 0

            lc += 6 + Math.max((12 * height / width), commentLength / 12)
            if (!workorder && lc > PDF_PAGE_SIZE) {
                docDefinition.content.push({ text: "", pageBreak: 'before' })
                lc = 0
            }

            const body = [
                defectHeader,
                [
                    item.title,
                    getStatusLabel(item.condition),
                    item.comment,
                    (photo ? { image: photo, width: 140 } : 'n/a'),
                ],
            ]

            if (workorder) {
                body.push([
                    'Signoff:',
                    'Date:',
                    'Responsible Person:',
                    'Corrective Action:\n\n\n\n\n\n\n\n\n\n\n\n',
                ])
            }

            docDefinition.content.push({
                style: 'tableExample',
                table: {
                    widths: [120, 60, 180, 140],
                    body,
                },
            })

            if (workorder && iInd + 1 < inspection.items.length) {
                docDefinition.content.push({ text: "", pageBreak: 'before' })
            }
        }

        docDefinition.content.push({
            text: 'This vehicle has been inspected in accordance with the following regulations O. Reg. 199/07 / CFR 392.7 / AR 121/2009',
            style: 'regFootnote',
        })

        docDefinition.content.push({
            text: `I ${inspectorName} confirm that this vehicle has been inspected in accordance with the applicable Regulation`,
            style: 'signatureLine',
        })

        if (signature) {
            docDefinition.content.push({ image: signature, width: 120, margin: [0, 4, 0, 4] })
        } else {
            docDefinition.content.push({ text: 'Signature: n/a', style: 'signatureLine' })
        }

        docDefinition.content.push({ text: 'Copyright CircleChek 2020', style: 'copyright' })

        if (ind + 1 < inspections.length) {
            docDefinition.content.push({ text: "", pageBreak: 'before' })
            lc = 0
        }

        reportProgress(currentInspection, currentInspection, 'inspection-complete')
    }

    const name = (vehicleData.unitNumber) + '-audit.pdf';
    reportProgress(totalInspections, totalInspections, 'download')
    pdfMake.createPdf(docDefinition).download(name);
}
